import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircle2, Loader2, SearchX, ShieldCheck } from 'lucide-react';

/*
 * Verificación pública de constancias (/verificar/:codigo).
 *
 * La abre un banco, una embajada o quien reciba la constancia, desde el QR o
 * escribiendo el código impreso. No tiene sesión: muestra los mismos datos que
 * trae la constancia para que los comparen con el papel.
 *
 * Usa fetch y no axiosInstance a propósito: el interceptor de axios manda a
 * /login ante un 401, y esta página no tiene sesión.
 */

const API = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '');

const limpiarCodigo = (texto) => (texto || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
const codigoConGuion = (codigo) => (codigo.length > 5 ? `${codigo.slice(0, 5)}-${codigo.slice(5)}` : codigo);

const formatFecha = (iso) => {
    if (!iso) return '';
    const [y, m, d] = String(iso).slice(0, 10).split('-');
    return `${d}/${m}/${y}`;
};

const formatCedula = (cedula) => {
    if (!cedula) return '';
    const digitos = cedula.replace(/\D/g, '');
    return `V-${Number(digitos).toLocaleString('es-VE')}`;
};

const formatMonto = (monto) => `Bs. ${Number(monto).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Marco = ({ children }) => (
    <div className="min-h-screen bg-slate-50 text-slate-900">
        <header className="bg-white border-b border-slate-200">
            <div className="max-w-xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
                <img src="/logo_sura.png" alt="SURA de Venezuela" className="h-7 w-auto" />
                <span className="text-sm text-slate-500">Verificación de constancias</span>
            </div>
        </header>
        <main className="max-w-xl mx-auto px-4 sm:px-6 py-8 sm:py-12">{children}</main>
    </div>
);

const Dato = ({ etiqueta, valor }) => (
    <div className="py-3 sm:grid sm:grid-cols-[11rem_1fr] sm:gap-4 border-t border-slate-100 first:border-t-0">
        <dt className="text-sm text-slate-500">{etiqueta}</dt>
        <dd className="mt-0.5 sm:mt-0 text-[15px] font-semibold text-slate-900 break-words">{valor}</dd>
    </div>
);

const FormularioCodigo = ({ inicial = '', onVerificar }) => {
    const [codigo, setCodigo] = useState(inicial);
    const limpio = limpiarCodigo(codigo);
    return (
        <form
            onSubmit={(e) => { e.preventDefault(); if (limpio.length >= 6) onVerificar(limpio); }}
            className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8"
        >
            <h1 className="text-xl font-bold text-slate-900">Verifica una constancia de trabajo</h1>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                Escribe el código que aparece debajo del QR, al pie de la constancia emitida por SURA de Venezuela.
            </p>
            <label htmlFor="codigo-verificacion" className="block mt-6 text-sm font-medium text-slate-700">
                Código de verificación
            </label>
            <input
                id="codigo-verificacion"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value)}
                placeholder="XXXXX-XXXXX"
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                className="mt-2 w-full px-4 py-3 text-lg tracking-[0.15em] tabular-nums uppercase rounded-xl border border-slate-300 focus:border-sura-blue focus:ring-2 focus:ring-sura-blue/30 outline-none"
            />
            <button
                type="submit"
                disabled={limpio.length < 6}
                className="mt-5 w-full sm:w-auto px-6 py-3 rounded-xl bg-sura-blue text-white font-semibold hover:bg-sura-blue-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sura-blue disabled:opacity-40 transition-colors"
            >
                Verificar
            </button>
        </form>
    );
};

const VerificarConstanciaView = () => {
    const { codigo: codigoUrl } = useParams();
    const navigate = useNavigate();
    const codigo = limpiarCodigo(codigoUrl);
    const [estado, setEstado] = useState(codigo ? 'cargando' : 'pedir');   // pedir | cargando | valida | invalida | error
    const [datos, setDatos] = useState(null);

    useEffect(() => {
        if (!codigo) { setEstado('pedir'); return undefined; }
        let activo = true;
        setEstado('cargando');
        fetch(`${API}/api/constancias-trabajo/verificar/${encodeURIComponent(codigo)}`)
            .then(async (r) => {
                if (!activo) return;
                if (r.status === 404) { setEstado('invalida'); return; }
                if (!r.ok) { setEstado('error'); return; }
                const d = await r.json();
                setDatos(d);
                setEstado(d.valida ? 'valida' : 'invalida');
            })
            .catch(() => { if (activo) setEstado('error'); });
        return () => { activo = false; };
    }, [codigo]);

    const verificar = (nuevo) => navigate(`/verificar/${codigoConGuion(nuevo)}`);

    if (estado === 'pedir') {
        return <Marco><FormularioCodigo onVerificar={verificar} /></Marco>;
    }

    if (estado === 'cargando') {
        return (
            <Marco>
                <div className="flex items-center justify-center gap-3 py-24 text-slate-500" role="status">
                    <Loader2 className="animate-spin" size={22} /> Verificando {codigoConGuion(codigo)}
                </div>
            </Marco>
        );
    }

    if (estado === 'invalida' || estado === 'error') {
        return (
            <Marco>
                <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
                    <div className="flex items-start gap-3">
                        <SearchX size={24} className="text-red-600 shrink-0 mt-0.5" />
                        <div>
                            <h1 className="text-xl font-bold text-slate-900">
                                {estado === 'error' ? 'No se pudo verificar en este momento' : 'Este código no corresponde a ninguna constancia'}
                            </h1>
                            <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                                {estado === 'error'
                                    ? 'Vuelve a intentarlo en unos minutos. Si sigue fallando, comunícate con Gestión Humana al 0241-3001900.'
                                    : <>El código <span className="font-semibold tabular-nums">{codigoConGuion(codigo)}</span> no está entre las constancias emitidas por SURA de Venezuela. Revisa que lo hayas copiado completo, tal como aparece debajo del QR.</>}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="mt-6">
                    <FormularioCodigo inicial={codigoConGuion(codigo)} onVerificar={verificar} />
                </div>
            </Marco>
        );
    }

    const esEgreso = datos.tipo === 'egreso';
    return (
        <Marco>
            <article className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <div className="px-6 sm:px-8 py-6 border-b border-slate-200 bg-emerald-50/60">
                    <div className="flex items-start gap-3">
                        <CheckCircle2 size={26} className="text-emerald-600 shrink-0 mt-0.5" />
                        <div>
                            <h1 className="text-xl font-bold text-slate-900">Constancia auténtica</h1>
                            <p className="mt-1 text-sm text-slate-600">
                                Emitida por SURA de Venezuela, C.A. el {formatFecha(datos.emitida)}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="px-6 sm:px-8 py-5">
                    <p className="text-sm text-slate-600 mb-2">
                        {esEgreso ? 'Constancia de trabajo de un extrabajador' : 'Constancia de trabajo'}, con estos datos:
                    </p>
                    <dl>
                        <Dato etiqueta="Nombre" valor={datos.nombre} />
                        <Dato etiqueta="Cédula" valor={formatCedula(datos.cedula)} />
                        <Dato etiqueta="Cargo" valor={datos.cargo || 'Sin cargo registrado'} />
                        <Dato etiqueta="Fecha de ingreso" valor={datos.ingreso} />
                        {datos.egreso && <Dato etiqueta="Fecha de egreso" valor={datos.egreso} />}
                        {datos.salario != null && (
                            <Dato etiqueta={esEgreso ? 'Salario mensual' : 'Sueldo mensual'} valor={formatMonto(datos.salario)} />
                        )}
                        {datos.dirigida_a && <Dato etiqueta="Presentada ante" valor={datos.dirigida_a} />}
                    </dl>
                </div>

                <div className="px-6 sm:px-8 py-4 border-t border-slate-200 bg-slate-50 flex items-center gap-2 text-sm">
                    <ShieldCheck size={17} className={datos.trabaja_actualmente ? 'text-emerald-600' : 'text-slate-500'} />
                    <span className="text-slate-800 font-medium">
                        {datos.trabaja_actualmente
                            ? 'Trabaja actualmente en SURA de Venezuela'
                            : datos.egreso ? `Egresó el ${datos.egreso}` : 'Ya no trabaja en SURA de Venezuela'}
                    </span>
                </div>
            </article>

            <p className="mt-6 text-sm text-slate-500 leading-relaxed">
                Código {codigoConGuion(datos.codigo)}. Si algún dato no coincide con el documento que tienes en la mano,
                comunícate con Gestión Humana al 0241-3001900.
            </p>
            <button
                type="button"
                onClick={() => navigate('/verificar')}
                className="mt-4 text-sm font-semibold text-sura-blue hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-sura-blue rounded"
            >
                Verificar otro código
            </button>
        </Marco>
    );
};

export default VerificarConstanciaView;
